import { GetServerSideProps, GetStaticProps } from 'next'
import { useRouter } from 'next/router'

import stripUndefined from '../../utils/stripUndefined'
import { getLayout, Layout as LayoutType } from '../../graphql/layout'
import { SiteLocale } from '../../graphql/datoSchema.generated'
import { BaseArticleFragment } from '../../graphql/articleList.generated'
import Layout from '../../components/Layout'
import BlockZone from '../../components/BlockZone'
import blocks from '../../blocks'
import { getPageTitle } from '../[[...slugs]]'
import { ArticleType, getArticleTypeList } from '../../graphql/articleTypeList'
import { getArticleList } from '../../graphql/articleList'
import { articlesPerPage } from '../../sections/NewsListingSection'
import { getSiteConfig, SiteConfig } from '../../graphql/siteConfig'
import { getPage, Page } from '../../graphql/page'
import { getSiteId } from '../../utils/env'
import absolutify from '../../utils/absolutify'
import stringifyQueryString from '../../utils/stringifyQueryString'
import PageSeo from '../../components/PageSeo'

export interface StaticProps {
  siteConfig: SiteConfig
  layout: LayoutType
  page: Page
  slug: string
  articleTypes: ArticleType[]
}

// next.js does not allow us to use both getServerSideProps and getStaticProps
// currently. This is coming, so I'll keep the structure in place.
// See https://github.com/vercel/next.js/discussions/11424#discussioncomment-2548409
const getStaticPropsFake: GetStaticProps<
  StaticProps,
  Record<string, never>
> = async ({ locale }) => {
  if (!locale) {
    throw new Error('Next.js is misconfigured: locale is missing')
  }

  const siteId = getSiteId()

  const [siteConfig, layout] = await Promise.all([
    getSiteConfig(siteId, locale as SiteLocale),
    getLayout(siteId, locale as SiteLocale),
  ])

  if (!siteConfig?.newsIndex?.slug) {
    throw new Error(
      'Ran into CMS misconfiguration: siteConfig.newsIndex.slug is missing',
    )
  }

  const [page, articleTypes] = await Promise.all([
    getPage(siteId, locale as SiteLocale, siteConfig.newsIndex.slug),
    getArticleTypeList(locale as SiteLocale),
  ])

  if (!page) {
    return {
      notFound: true,
      // TODO: Configure via environment variables
      revalidate: 60,
    }
  }

  return {
    props: stripUndefined({
      siteConfig,
      layout,
      page,
      slug: siteConfig.newsIndex.slug,
      articleTypes,
    }),
    // TODO: Configure via environment variables
    revalidate: 60,
  }
}

interface ServerProps {
  articles: BaseArticleFragment[]
  numTotalArticles: number
  currentPage: number
  activeFilter?: string
}

export const getServerSideProps: GetServerSideProps<
  ServerProps,
  Record<string, never>
> = async (context) => {
  const staticProps = await getStaticPropsFake(context)
  if ('redirect' in staticProps) {
    return staticProps
  }
  if ('notFound' in staticProps) {
    // We are not allowed to return `revalidate` in this function
    return {
      notFound: true,
    }
  }

  const {
    res,
    query: { page, filter },
    locale,
  } = context
  if (!locale) {
    throw new Error('Next.js is misconfigured: locale is missing')
  }

  const typeFilter = staticProps.props.articleTypes.find(
    ({ title }) => title === filter,
  )

  const { articles, count } = await getArticleList(
    getSiteId(),
    locale as SiteLocale,
    typeFilter?.id,
    undefined,
    articlesPerPage,
    typeof page === 'string' ? Number(page) : undefined,
  )

  res.setHeader(
    'Cache-Control',
    'public, s-maxage=60, stale-while-revalidate=600',
  )

  return {
    props: stripUndefined({
      ...staticProps.props,
      articles,
      numTotalArticles: count,
      activeFilter: typeFilter?.title || undefined,
      currentPage: Number(page) || 1,
    }),
  }
}

const useCanonical = (slug: string, page: number, filter?: string) => {
  const { locale } = useRouter()
  if (!locale) {
    throw new Error('locale is missing')
  }

  return absolutify(
    filter || page > 1
      ? `/${slug}?${stringifyQueryString(
          stripUndefined({
            filter,
            page: page > 1 ? page : undefined,
          }),
        )}`
      : `/${slug}`,
    locale as SiteLocale,
  )
}

const NewsIndex = ({
  siteConfig,
  layout,
  page,
  slug,
  articleTypes,
  articles,
  numTotalArticles,
  currentPage,
  activeFilter,
}: StaticProps & ServerProps) => {
  const title = getPageTitle(page)
  const canonical = useCanonical(slug, currentPage, activeFilter)

  return (
    <Layout
      siteConfig={siteConfig}
      layout={layout}
      breadcrumbs={[
        {
          key: 'homepage',
          href: '/',
          title: siteConfig.homepageName || 'Home',
        },
        {
          key: 'news',
          href: `/${slug}`,
          title: title || '?',
        },
      ]}
    >
      <PageSeo page={page} canonical={canonical} />

      <BlockZone
        components={blocks}
        content={page.content}
        data={{
          NewsListingSection: {
            articleTypes,
            articles,
            numTotalArticles,
            activeFilter,
            currentPage,
          },
        }}
      />
    </Layout>
  )
}

export default NewsIndex
